/**
 * Applier for the flat workflow (BPMN) collab ops.
 *
 * `applyWfOps(doc, ops)` mutates `doc` in place, applying ops sequentially
 * (matching sd-apply's in-place convention). An op whose target no longer
 * exists is SKIPPED into `skippedOps` as `{ opId, code }` — never thrown:
 * collab op streams race against concurrent edits and echoes, and a skipped
 * op is a normal outcome. A skipped op changes NOTHING — prop sets are
 * pre-validated read-only so a failing pointer never leaves `{}` husks behind
 * (the inverter's "skipped ops need no inverse" contract depends on this).
 * The one exception to skipping is `wfElementRemove` on a missing target,
 * which is an ACCEPTED NO-OP (counted as applied, no skip entry) — two
 * clients deleting the same element must both converge as winners.
 *
 * Skip codes: NODE_GONE (element-scoped op on a missing element), PARENT_GONE
 * (upsert/containerSet whose container is missing), CYCLE (containment cycle),
 * TARGET_GONE (flow endpoint or attach host missing), BAD_PATH (unresolvable
 * or non-whitelisted pointer), BAD_OP (malformed op).
 *
 * `elementOrder` maintenance (canonical invariant, see wf-types.ts):
 *  - upsert INSERT appends the id to its container's bucket;
 *  - upsert REPLACE keeps the element's slot when `containerId` is unchanged,
 *    else moves it to the tail of the new container's bucket;
 *  - containerSet detaches FIRST, then resolves `afterSiblingId` against the
 *    post-detach bucket (null = first, missing/absent = append) — mirroring
 *    sd-apply's insert-after-sibling exactly;
 *  - remove drops the element from every bucket and deletes its own bucket.
 *
 * laneId: client diffs never author it; a `wfElementUpsert` carrying laneId
 * applies it verbatim, and the SERVER-ORIGIN `wfLaneDerived {elementId, laneId}`
 * op (broadcast after the server re-derives lane membership from geometry)
 * applies the new laneId (null = no lane). Clients apply it, never emit it.
 *
 * `/name` (exact single-segment pointer) on wfPropSet/wfPropRemove routes to
 * the TOP-LEVEL `element.name` — `semantics` never carries `name` (upserts
 * strip it defensively); deeper "/name/…" pointers are BAD_PATH.
 */

import { cloneJsonValue, CollabJsonValue, ICollabJsonObject, isJsonArray, isJsonObject } from "../sd-patch/sd-json";
import {
  ARRAY_INDEX_PATTERN,
  CollabPathSegment,
  findSelectorIndex,
  parsePointer,
  removeAtPath,
  setAtPath
} from "../sd-patch/sd-path";
import {
  appendToOrderBucket,
  containerChainReaches,
  ensureDocShape,
  insertIntoOrderBucket,
  isNonEmptyString,
  removeFromAllOrderBuckets
} from "./wf-doc";
import { IWfApplyResult, IWfCollabOp, IWfSkippedOp, WfApplySkipCode } from "./wf-op-types";
import { IWfBounds, IWfDocument, IWfElement, IWfPoint } from "./wf-types";

/** Doc roots writable through wfDocPropSet/wfDocPropRemove — everything else is refused. */
const WF_DOC_PROP_ROOTS: ReadonlySet<string> = new Set<string>([
  "name",
  "parameters",
  "happyPathDefinition",
  "docExtras"
]);

type WfApplyOutcome = "applied" | WfApplySkipCode;

function cloneElement(element: IWfElement): IWfElement {
  return cloneJsonValue(element as unknown as ICollabJsonObject) as unknown as IWfElement;
}

function applyWfElementUpsert(doc: IWfDocument, op: IWfCollabOp): WfApplyOutcome {
  const payload: IWfElement | undefined = op.element;
  if (payload === undefined || !isJsonObject(payload as unknown)) {
    return "BAD_OP";
  }
  if (!isNonEmptyString(payload.id)) {
    return "BAD_OP";
  }
  if (op.elementId !== undefined && op.elementId !== payload.id) {
    return "BAD_OP";
  }
  if (typeof payload.containerId !== "string") {
    return "BAD_OP";
  }
  const elementId: string = payload.id;
  const containerId: string = payload.containerId;
  if (containerId !== "" && doc.elements[containerId] === undefined) {
    return "PARENT_GONE";
  }
  if (containerChainReaches(doc, containerId, elementId)) {
    return "CYCLE";
  }
  const cloned: IWfElement = cloneElement(payload);
  if (!isJsonObject(cloned.semantics as unknown)) {
    cloned.semantics = {};
  }
  // Contract: `semantics` must never carry `name` — the element name lives
  // TOP-LEVEL (element.name) on both engines. Self-healing strip keeps docs
  // canonical even when a payload smuggles one in (the C# applier strips too).
  delete cloned.semantics["name"];
  const existing: IWfElement | undefined = doc.elements[elementId];
  doc.elements[elementId] = cloned;
  if (existing === undefined) {
    appendToOrderBucket(doc, containerId, elementId);
    return "applied";
  }
  if (existing.containerId !== containerId) {
    removeFromAllOrderBuckets(doc, elementId);
    appendToOrderBucket(doc, containerId, elementId);
  } else {
    // Same container: keep the slot (defensive append when the bucket lost it).
    appendToOrderBucket(doc, containerId, elementId);
  }
  return "applied";
}

function applyWfElementRemove(doc: IWfDocument, op: IWfCollabOp): WfApplyOutcome {
  if (!isNonEmptyString(op.elementId)) {
    return "BAD_OP";
  }
  const ids: string[] = [op.elementId];
  if (Array.isArray(op.cascadedIds)) {
    for (const cascadedId of op.cascadedIds) {
      if (isNonEmptyString(cascadedId)) {
        ids.push(cascadedId);
      }
    }
  }
  for (const id of ids) {
    if (doc.elements[id] === undefined) {
      continue; // Accepted no-op: already gone.
    }
    delete doc.elements[id];
    removeFromAllOrderBuckets(doc, id);
    delete doc.elementOrder[id];
  }
  return "applied";
}

function applyWfGeometrySet(doc: IWfDocument, op: IWfCollabOp): WfApplyOutcome {
  if (!isNonEmptyString(op.elementId)) {
    return "BAD_OP";
  }
  if (op.bounds === undefined && op.waypoints === undefined && op.labelBounds === undefined) {
    return "BAD_OP";
  }
  const element: IWfElement | undefined = doc.elements[op.elementId];
  if (element === undefined) {
    return "NODE_GONE";
  }
  if (op.bounds !== undefined) {
    element.bounds = cloneJsonValue(op.bounds as unknown as ICollabJsonObject) as unknown as IWfBounds;
  }
  if (op.waypoints !== undefined) {
    element.waypoints = cloneJsonValue(op.waypoints) as unknown as IWfPoint[];
  }
  if (op.labelBounds !== undefined) {
    if (op.labelBounds === null) {
      element.labelBounds = null;
    } else {
      element.labelBounds = cloneJsonValue(op.labelBounds as unknown as ICollabJsonObject) as unknown as IWfBounds;
    }
  }
  return "applied";
}

function applyWfFlowEndpointsSet(doc: IWfDocument, op: IWfCollabOp): WfApplyOutcome {
  if (!isNonEmptyString(op.elementId) || !isNonEmptyString(op.sourceId) || !isNonEmptyString(op.targetId)) {
    return "BAD_OP";
  }
  const element: IWfElement | undefined = doc.elements[op.elementId];
  if (element === undefined) {
    return "NODE_GONE";
  }
  if (doc.elements[op.sourceId] === undefined || doc.elements[op.targetId] === undefined) {
    return "TARGET_GONE";
  }
  element.sourceId = op.sourceId;
  element.targetId = op.targetId;
  return "applied";
}

function applyWfAttachSet(doc: IWfDocument, op: IWfCollabOp): WfApplyOutcome {
  if (!isNonEmptyString(op.elementId) || !isNonEmptyString(op.hostId)) {
    return "BAD_OP";
  }
  if (op.hostId === op.elementId) {
    return "BAD_OP";
  }
  const element: IWfElement | undefined = doc.elements[op.elementId];
  if (element === undefined) {
    return "NODE_GONE";
  }
  if (doc.elements[op.hostId] === undefined) {
    return "TARGET_GONE";
  }
  element.attachedToId = op.hostId;
  return "applied";
}

function applyWfContainerSet(doc: IWfDocument, op: IWfCollabOp): WfApplyOutcome {
  if (!isNonEmptyString(op.elementId) || typeof op.containerId !== "string") {
    return "BAD_OP";
  }
  const element: IWfElement | undefined = doc.elements[op.elementId];
  if (element === undefined) {
    return "NODE_GONE";
  }
  const containerId: string = op.containerId;
  if (containerId !== "" && doc.elements[containerId] === undefined) {
    return "PARENT_GONE";
  }
  if (containerChainReaches(doc, containerId, op.elementId)) {
    return "CYCLE";
  }
  // Detach first, then resolve the anchor against the post-detach bucket —
  // this is what makes same-container reorders land on the right slot.
  removeFromAllOrderBuckets(doc, op.elementId);
  element.containerId = containerId;
  insertIntoOrderBucket(doc, containerId, op.elementId, op.afterSiblingId);
  return "applied";
}

function ensureSemantics(element: IWfElement): Record<string, unknown> {
  if (!isJsonObject(element.semantics as unknown)) {
    element.semantics = {};
  }
  return element.semantics;
}

function parseElementPropPath(path: string | undefined): CollabPathSegment[] | null {
  if (typeof path !== "string") {
    return null;
  }
  const segments: CollabPathSegment[] | null = parsePointer(path);
  if (segments === null || segments.length === 0) {
    return null;
  }
  return segments;
}

/**
 * Whether `setAtPath(root, segments, …)` would SUCCEED — without mutating.
 * setAtPath creates `{}` intermediates while walking and only then can fail on
 * a later segment, leaving husks behind; a SKIPPED op must change nothing, so
 * the applier pre-validates with this read-only twin of the walk. "Virtual"
 * mode models walking inside the would-be-created objects: only plain segments
 * can succeed there (a selector or array index can never resolve inside `{}`).
 */
function canSetAtPath(root: unknown, segments: readonly CollabPathSegment[]): boolean {
  let current: unknown = root;
  let virtual: boolean = false;
  for (let i = 0; i < segments.length - 1; i++) {
    const segment: CollabPathSegment = segments[i];
    if (virtual) {
      if (segment.kind !== "plain") {
        return false;
      }
      continue;
    }
    if (segment.kind === "selector") {
      if (!isJsonArray(current)) {
        return false;
      }
      const selectorIndex: number = findSelectorIndex(current, segment);
      if (selectorIndex < 0) {
        return false;
      }
      current = current[selectorIndex];
    } else if (isJsonArray(current)) {
      if (!ARRAY_INDEX_PATTERN.test(segment.key)) {
        return false;
      }
      const index: number = Number(segment.key);
      if (index >= current.length) {
        return false;
      }
      current = current[index];
    } else if (isJsonObject(current)) {
      const existing: CollabJsonValue = current[segment.key];
      if (existing === undefined || existing === null) {
        virtual = true;
      } else {
        current = existing;
      }
    } else {
      return false;
    }
  }
  const last: CollabPathSegment = segments[segments.length - 1];
  if (virtual) {
    return last.kind === "plain";
  }
  if (last.kind === "selector") {
    return isJsonArray(current);
  }
  if (isJsonArray(current)) {
    if (!ARRAY_INDEX_PATTERN.test(last.key)) {
      return false;
    }
    return Number(last.key) <= current.length;
  }
  return isJsonObject(current);
}

/**
 * Whether the pointer's FIRST segment addresses the top-level `name` field.
 * "/name" routes to `element.name` (never `semantics.name` — cross-language
 * contract); any DEEPER "/name/…" pointer is BAD_PATH on both engines, because
 * `name` is a scalar and nesting under it would otherwise create a `semantics`
 * husk on one side only.
 */
function isNameRootedPath(segments: readonly CollabPathSegment[]): boolean {
  return segments[0].kind === "plain" && segments[0].key === "name";
}

function applyWfPropSet(doc: IWfDocument, op: IWfCollabOp): WfApplyOutcome {
  if (!isNonEmptyString(op.elementId)) {
    return "BAD_OP";
  }
  const segments: CollabPathSegment[] | null = parseElementPropPath(op.path);
  if (segments === null) {
    return "BAD_PATH";
  }
  const element: IWfElement | undefined = doc.elements[op.elementId];
  if (element === undefined) {
    return "NODE_GONE";
  }
  if (isNameRootedPath(segments)) {
    if (segments.length !== 1) {
      return "BAD_PATH";
    }
    if (typeof op.value !== "string") {
      return "BAD_OP";
    }
    element.name = op.value;
    return "applied";
  }
  const semantics: Record<string, unknown> = ensureSemantics(element);
  if (!canSetAtPath(semantics, segments)) {
    return "BAD_PATH";
  }
  const success: boolean = setAtPath(semantics, op.path as string, cloneJsonValue(op.value));
  return success ? "applied" : "BAD_PATH";
}

function applyWfPropRemove(doc: IWfDocument, op: IWfCollabOp): WfApplyOutcome {
  if (!isNonEmptyString(op.elementId)) {
    return "BAD_OP";
  }
  const segments: CollabPathSegment[] | null = parseElementPropPath(op.path);
  if (segments === null) {
    return "BAD_PATH";
  }
  const element: IWfElement | undefined = doc.elements[op.elementId];
  if (element === undefined) {
    return "NODE_GONE";
  }
  if (isNameRootedPath(segments)) {
    if (segments.length !== 1 || element.name === undefined) {
      return "BAD_PATH";
    }
    delete element.name;
    return "applied";
  }
  const semantics: Record<string, unknown> = ensureSemantics(element);
  const success: boolean = removeAtPath(semantics, op.path as string);
  return success ? "applied" : "BAD_PATH";
}

function applyWfLaneDerived(doc: IWfDocument, op: IWfCollabOp): WfApplyOutcome {
  if (!isNonEmptyString(op.elementId)) {
    return "BAD_OP";
  }
  if (op.laneId === undefined || (op.laneId !== null && typeof op.laneId !== "string")) {
    return "BAD_OP";
  }
  const element: IWfElement | undefined = doc.elements[op.elementId];
  if (element === undefined) {
    return "NODE_GONE";
  }
  element.laneId = op.laneId;
  return "applied";
}

function parseDocPropPath(path: string | undefined): CollabPathSegment[] | null {
  if (typeof path !== "string") {
    return null;
  }
  const segments: CollabPathSegment[] | null = parsePointer(path);
  if (segments === null || segments.length === 0) {
    return null;
  }
  const first: CollabPathSegment = segments[0];
  if (first.kind !== "plain" || !WF_DOC_PROP_ROOTS.has(first.key)) {
    return null;
  }
  return segments;
}

/** Whether the pointer's FIRST segment addresses the participantBounds doc root. */
function isParticipantBoundsRootedPath(segments: readonly CollabPathSegment[]): boolean {
  return segments[0].kind === "plain" && segments[0].key === "participantBounds";
}

/**
 * Decodes a wire value as participant bounds, mirroring the C# codec exactly
 * (WfWireCodec.TryDecodeBounds): an object with finite numeric x/y/width/height;
 * extra keys are TOLERATED but DROPPED — both engines must store the identical
 * canonical four-key rectangle or their docs silently diverge.
 */
function decodeWfBoundsValue(value: unknown): IWfBounds | null {
  if (!isJsonObject(value)) {
    return null;
  }
  const x: unknown = value["x"];
  const y: unknown = value["y"];
  const width: unknown = value["width"];
  const height: unknown = value["height"];
  if (
    typeof x !== "number" ||
    typeof y !== "number" ||
    typeof width !== "number" ||
    typeof height !== "number" ||
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height)
  ) {
    return null;
  }
  return { x, y, width, height };
}

function applyWfDocPropSet(doc: IWfDocument, op: IWfCollabOp): WfApplyOutcome {
  if (typeof op.path === "string") {
    const rawSegments: CollabPathSegment[] | null = parsePointer(op.path);
    if (rawSegments !== null && rawSegments.length > 0 && isParticipantBoundsRootedPath(rawSegments)) {
      // participantBounds is EXACT-ROOT + WHOLE-VALUE by contract (see wf-types):
      // deeper pointers would let racing clients converge to a mixed rectangle.
      if (rawSegments.length !== 1) {
        return "BAD_PATH";
      }
      const bounds: IWfBounds | null = decodeWfBoundsValue(op.value);
      if (bounds === null) {
        return "BAD_OP";
      }
      doc.participantBounds = bounds;
      return "applied";
    }
  }
  const segments: CollabPathSegment[] | null = parseDocPropPath(op.path);
  if (segments === null) {
    return "BAD_PATH";
  }
  if (!canSetAtPath(doc, segments)) {
    return "BAD_PATH";
  }
  const success: boolean = setAtPath(doc, op.path as string, cloneJsonValue(op.value));
  return success ? "applied" : "BAD_PATH";
}

function applyWfDocPropRemove(doc: IWfDocument, op: IWfCollabOp): WfApplyOutcome {
  if (typeof op.path === "string") {
    const rawSegments: CollabPathSegment[] | null = parsePointer(op.path);
    if (rawSegments !== null && rawSegments.length > 0 && isParticipantBoundsRootedPath(rawSegments)) {
      // A participant without bounds is unprojectable XML (DI completeness is
      // authoritative-required on the C# parser) — removal is refused on BOTH
      // engines so the vocabulary cannot express the poisoned state.
      return "BAD_PATH";
    }
  }
  if (parseDocPropPath(op.path) === null) {
    return "BAD_PATH";
  }
  const success: boolean = removeAtPath(doc, op.path as string);
  return success ? "applied" : "BAD_PATH";
}

function applyOneWfOp(doc: IWfDocument, op: IWfCollabOp): WfApplyOutcome {
  switch (op.kind) {
    case "wfElementUpsert":
      return applyWfElementUpsert(doc, op);
    case "wfElementRemove":
      return applyWfElementRemove(doc, op);
    case "wfGeometrySet":
      return applyWfGeometrySet(doc, op);
    case "wfFlowEndpointsSet":
      return applyWfFlowEndpointsSet(doc, op);
    case "wfAttachSet":
      return applyWfAttachSet(doc, op);
    case "wfContainerSet":
      return applyWfContainerSet(doc, op);
    case "wfPropSet":
      return applyWfPropSet(doc, op);
    case "wfPropRemove":
      return applyWfPropRemove(doc, op);
    case "wfDocPropSet":
      return applyWfDocPropSet(doc, op);
    case "wfDocPropRemove":
      return applyWfDocPropRemove(doc, op);
    case "wfLaneDerived":
      return applyWfLaneDerived(doc, op);
    default:
      return "BAD_OP";
  }
}

export function applyWfOps(doc: IWfDocument, ops: IWfCollabOp[]): IWfApplyResult {
  ensureDocShape(doc);
  const skippedOps: IWfSkippedOp[] = [];
  let appliedCount: number = 0;
  for (const op of ops) {
    const outcome: WfApplyOutcome = applyOneWfOp(doc, op);
    if (outcome === "applied") {
      appliedCount++;
    } else {
      skippedOps.push({ opId: op.opId, code: outcome });
    }
  }
  return { appliedCount, skippedOps };
}
